import * as React from 'react'
import dynamic from 'next/dynamic'

import {
  PricePerUnit,
  ProductClearance,
} from '@thg-commerce/enterprise-components'
import { pricePerUnitText } from '@thg-commerce/enterprise-components/PricePerUnit/PricePerUnit'
import { getPowerReviewGroups } from '@thg-commerce/enterprise-components/src/PowerReview/PowerReview'
import {
  EnterpriseContext,
  i18n,
  LIST_EXTENSION,
  useExperiments,
  useFormattableI18nProperty,
  useSiteConfig,
  useSiteDefinition,
  useTransmit,
} from '@thg-commerce/enterprise-core'
import { useBasket } from '@thg-commerce/enterprise-core/src/Basket/hooks/useBasketId'
import { getColumboEventData } from '@thg-commerce/enterprise-metrics'
import { EnterpriseProductSummaryDetails } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductSummaryDetails'
import { ProductVariantWithDescription } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Product'
import { NormalisedBasket } from '@thg-commerce/enterprise-network/src/transformers/basket'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { getPrice } from '@thg-commerce/enterprise-product-options/src/utils/price'
import {
  BrandLogo,
  BrandSize,
  Price,
  Separator,
} from '@thg-commerce/gravity-elements'

import { useProductPage } from '../../context'
import { ProductTagsRenderer, ProofOfImpactIconRenderer } from '../../renderers'
import { ExternalIdentifierDisplay } from '../ExternalIdentifierDisplay'
import { LoyaltyPointsMessage } from '../LoyaltyPointsMessage'
import { PowerReviewReviewSnippetProps } from '../PowerReviewReviewSnippet/PowerReviewReviewSnippet'

import {
  DetailsContainer,
  PowerReviewReviewSnippetContainer,
  PriceContainer,
  ProofOfImpactIconWrapper,
  ReviewContainer,
  StyledProductReview,
  SubTitle,
  TagsRendererWrapper,
  Title,
} from './styles'

const PowerReviewReviewSnippet = dynamic<PowerReviewReviewSnippetProps>(() =>
  import('../../components/PowerReviewReviewSnippet').then(
    (mod) => mod.PowerReviewReviewSnippet,
  ),
)

export interface ProductSummaryDetails {
  productSummaryDetails: EnterpriseProductSummaryDetails
}

export interface ProductSummaryPrice {
  productSummaryPrice: {
    price: string
    rrp: string
    cheapestVariantPrice: {
      price: string
      rrp: string
    }
  }
}

const INVALID_CONTRACT_ID = '15'

const getSubscriptionPrice = (
  basket: NormalisedBasket | undefined,
  selectedVariant: ProductVariantWithDescription,
  defaultPrice?: string,
) => {
  if (!basket || !selectedVariant) return undefined

  const matchingItem = basket.items.find(
    (item) => item.product.sku === selectedVariant.sku,
  )

  const selectedFrequencyId =
    matchingItem?.subscriptionContract?.id ||
    selectedVariant?.subscriptionContracts?.[0]?.id ||
    INVALID_CONTRACT_ID

  return (
    selectedVariant?.subscriptionContracts?.find(
      (contract) => contract.id === selectedFrequencyId,
    )?.initialPrice?.price?.displayValue ||
    defaultPrice ||
    'N/A'
  )
}

const getDisplayPriceAboveQuantityAndCheckConstituentLink = (
  isConstituentProduct: boolean,
  displayPriceAboveQuantity?: boolean,
) => {
  return !displayPriceAboveQuantity && !isConstituentProduct
}
const getDisplayThemedSeperator = (
  displaySeparator: boolean,
  isConstituentProduct: boolean,
) => {
  return displaySeparator && !isConstituentProduct
}

type pricePerUnit = {
  displayValue: string
  unit: string
  subscriptionDisplayValue?: string
} | null

const getDisplayPricePerUnit = (
  selectedVariantPPU: ProductVariantWithDescription['pricePerUnit'],
  productPPU?: pricePerUnit,
): pricePerUnitText => {
  if (!productPPU) return null

  const result = {
    unit: productPPU.unit,
    displayValue: productPPU.displayValue,
    subscriptionDisplayValue: productPPU.subscriptionDisplayValue,
  }

  if (selectedVariantPPU?.standardPpu) {
    result.displayValue = selectedVariantPPU.standardPpu
  }

  if (selectedVariantPPU?.subscriptionPpu) {
    result.subscriptionDisplayValue = selectedVariantPPU.subscriptionPpu
  }

  return result
}

interface ProductSummaryInterface {
  children?: JSX.Element | null
}

const getTitleClassName = (hasShoeSizeMeId) => {
  return hasShoeSizeMeId ? 'productName_title' : undefined
}

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = React.useState(false)

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkIfDesktop = () => {
        const newIsDesktop = window.innerWidth > 768
        setIsDesktop(newIsDesktop)
      }

      checkIfDesktop()

      window.addEventListener('resize', checkIfDesktop)

      return () => window.removeEventListener('resize', checkIfDesktop)
    }
  }, [])

  return isDesktop
}

interface ProductHeaderProps {
  displayMasterSKUTitle: boolean
  selectedVariant: ProductVariantWithDescription | null
  product: EnterpriseProductSummaryDetails
  variant: ProductVariantWithDescription | EnterpriseProductSummaryDetails
  hasShoeSizeMeId: boolean
  isDesktop: boolean
  showTagAndSubtitleBelowCta: boolean
  subtitle: string | null
  theme: any
  showFlagsOnProduct: boolean
}

const ProductHeader: React.FC<ProductHeaderProps> = ({
  displayMasterSKUTitle,
  selectedVariant,
  product,
  variant,
  hasShoeSizeMeId,
  isDesktop,
  showTagAndSubtitleBelowCta,
  subtitle,
  theme,
  showFlagsOnProduct,
}) => (
  <React.Fragment>
    <Title
      textStyle={theme.pageTheme?.productSummary?.title?.textStyle}
      order={theme.pageTheme?.productSummary?.title?.order}
      data-testid="product-title"
      className={getTitleClassName(hasShoeSizeMeId)}
      styleOverride={theme.pageTheme.productSummary.title?.styleOverride}
    >
      {displayMasterSKUTitle
        ? selectedVariant?.masterSkuTitle || product?.title
        : variant.title}
    </Title>

    {(isDesktop || !showTagAndSubtitleBelowCta) && (
      <TagsAndSubtitle theme={theme} subtitle={subtitle} />
    )}

    <ExternalIdentifierDisplay
      order={theme.pageTheme?.productSummary?.externalIdentifierDisplay?.order}
      product={product}
      selectedVariant={selectedVariant}
      textStyle={
        theme.pageTheme?.productSummary?.externalIdentifierDisplay?.textStyle
      }
    />

    {showFlagsOnProduct && (
      <ProductClearance
        content={product?.content}
        order={theme.pageTheme?.productSummary?.productClearance?.order}
      />
    )}

    <ProofOfImpactIconWrapper
      order={theme.pageTheme?.productSummary?.proofOfImpactIcon?.order}
    >
      <ProofOfImpactIconRenderer productTitle={variant.title} />
    </ProofOfImpactIconWrapper>
  </React.Fragment>
)

const TagsAndSubtitle = ({ theme, subtitle }) => (
  <>
    <TagsRendererWrapper order={theme.pageTheme?.productSummary?.tags?.order}>
      <ProductTagsRenderer />
    </TagsRendererWrapper>
    {subtitle && (
      <SubTitle
        textStyle={theme?.pageTheme?.productSummary?.subtitle?.textStyle}
        content={subtitle}
        order={theme.pageTheme?.productSummary?.subtitle?.order}
      />
    )}
  </>
)

const ProductReviews = ({
  product,
  selectedVariant,
  showPowerReview,
  defaultLocale,
  theme,
  onProductReviewClick,
  i18nText,
}) => {
  if (selectedVariant && product.url && showPowerReview) {
    return (
      <PowerReviewReviewSnippetContainer
        displayStyle={
          theme.patterns.productBlock?.components?.powerReviewCategorySnippet
            ?.displayStyle
        }
      >
        <PowerReviewReviewSnippet
          sku={selectedVariant.sku}
          externalId={Number(selectedVariant.externalIdentifier)}
          locale={defaultLocale}
          pathname={product.url}
          powerReviewGroups={getPowerReviewGroups(product.content)}
        />
      </PowerReviewReviewSnippetContainer>
    )
  }

  if (!showPowerReview && product.reviews && product.reviews.total >= 1) {
    return (
      <ReviewContainer>
        <StyledProductReview
          starRating={product.reviews.averageScore}
          numberOfReviews={product.reviews.total}
          onProductReviewClick={onProductReviewClick}
          screenReaderOnlyText={`${product.reviews.averageScore} ${
            i18nText.starsLabel
          }. ${product.reviews.total} ${
            product.reviews.total === 1
              ? i18nText.reviewLabel
              : i18nText.reviewsLabel
          }`}
          reviewsCounterSuffixText={
            product.reviews.total === 1
              ? i18nText.reviewsCounterSingleSuffix
              : i18nText.reviewsCounterSuffix
          }
          reviewCounterTextStyle={
            theme.pageTheme.productRating?.averageStars?.font
          }
          reviewLinkUrl={product.url.replace('.html', '.reviews')}
          colorFills={{
            fill:
              theme.pageTheme?.productRating?.averageStars?.colorFills?.fill,
            background:
              theme.pageTheme?.productRating?.averageStars?.colorFills
                ?.background,
          }}
        />
      </ReviewContainer>
    )
  }

  return null
}

const ProductPrice = ({
  displayPriceAboveQuantityAndCheckConstituentLink,
  hidePDPBuyingComponents,
  papMessageTopPosition,
  theme,
  selectedVariant,
  currency,
  defaultLocale,
  activeTabIndex,
  selectedContractDiscountedPrice,
  showProductSummaryPriceOnSingleLine,
  i18nText,
  enablePricePerUnit,
  displayPPU,
  displayThemedSeperator,
  displayPriceContainerSeparators,
  showPercentageDiscount,
}) => {
  return (
    <React.Fragment>
      {displayPriceContainerSeparators()}

      {displayPriceAboveQuantityAndCheckConstituentLink &&
        !hidePDPBuyingComponents && (
          <PriceContainer reduceBottomMargin={papMessageTopPosition}>
            <Price
              priceTheme={{
                price: theme.pageTheme.productSummary.price.price,
                rrp: theme.pageTheme.productSummary.price.rrp,
                savings: theme.pageTheme.productSummary.price.savings,
                priceWithRRP: theme.pageTheme.productSummary.price.priceWithRRP,
              }}
              priceDirection={theme.pageTheme.productSummary.price.direction}
              displayRRPText={
                theme.pageTheme.productSummary.price.displayRRPText
              }
              pricing={getPrice(
                selectedVariant,
                currency,
                defaultLocale,
                activeTabIndex,
                selectedContractDiscountedPrice,
              )}
              savingsPercentage={
                showPercentageDiscount
                  ? { show: true }
                  : theme.pageTheme.productSummary.price.savingsPercentage
              }
              showProductSummaryPriceOnSingleLine={
                showProductSummaryPriceOnSingleLine
              }
              i18nText={{
                rrpLabel: i18nText.rrpLabel,
                saveLabel: i18nText.saveLabel,
                offLabel: i18nText.offLabel,
              }}
            />
          </PriceContainer>
        )}

      {enablePricePerUnit && !hidePDPBuyingComponents && (
        <PricePerUnit
          pricePerUnit={displayPPU}
          activeTabIndex={activeTabIndex}
        />
      )}

      {displayThemedSeperator && (
        <Separator
          separatorMargin={theme.pageTheme.productSummary?.separatorMargin}
        />
      )}

      {displayPriceContainerSeparators()}
    </React.Fragment>
  )
}

export const ProductSummary = ({ ...props }: ProductSummaryInterface) => {
  const EnterpriseCtx = React.useContext(EnterpriseContext)
  const { product, selectedVariant, activeTabIndex } = useProductPage()
  const { basket } = useBasket()
  const { currency } = EnterpriseCtx
  const {
    originUrl,
    defaultLocale,
    subsite,
    siteId,
    channel,
  } = useSiteDefinition()
  const transmit = useTransmit()
  const propertyArgs = {
    defaultLocale,
    subsite,
    siteId,
    channel,
    customerLocation: EnterpriseCtx.currentLocation,
  }
  const {
    hasLoyaltyEnabled,
    PDPDisplayMasterProductTitle,
    showPowerReview,
    papMessageTopPosition,
    enablePricePerUnit,
    enableProductBrandPageLink,
    showFlagsOnProduct,
    displayPriceAboveQuantity,
    displayMasterSKUTitle,
    showBrandLogoOnProductPage,
    showProductSummaryPriceOnSingleLine,
    hidePDPBuyingComponents,
    hasShoeSizeMeId,
    showTagAndSubtitleBelowCta,
  } = useSiteConfig()

  const isDesktop = useIsDesktop()
  const theme = useTheme()
  const experiments = useExperiments()

  const brandLogoLabel = useFormattableI18nProperty('product.brand.logo.label')
  const brandUrl = useFormattableI18nProperty('product.brand.logo.url')

  const showPercentageDiscount =
    experiments['price_rrp_percentage'] === 'v1|show_percentage_discount'

  const displayPPU = getDisplayPricePerUnit(
    selectedVariant.pricePerUnit,
    product.pricePerUnit,
  )

  const displayPriceContainerSeparators = () => {
    if (theme.pageTheme.productSummary.price.displayPriceContainerSeparators) {
      return <Separator withSpacing />
    }
    return null
  }

  const i18nText = React.useMemo(
    () => ({
      brandLogoLabel,
      brandUrl,
      rrpLabel: i18n('general.rrp.text'),
      saveLabel: i18n('general.save.text'),
      offLabel: i18n('general.off.text'),
      starsLabel: i18n('reviews.stars.label'),
      reviewsLabel: i18n('reviews.reviews.label'),
      reviewLabel: i18n('reviews.review.label'),
      reviewsCounterSingleSuffix: i18n(
        'reviews.reviewscountersinglesuffix.text',
      ),
      reviewsCounterSuffix: i18n('reviews.reviewscountersuffix.text'),
    }),
    [brandLogoLabel, brandUrl],
  )

  const displayPriceAboveQuantityAndCheckConstituentLink = getDisplayPriceAboveQuantityAndCheckConstituentLink(
    product.constituentProduct.isConstituentProduct,
    displayPriceAboveQuantity,
  )

  const displayThemedSeperator = getDisplayThemedSeperator(
    theme.pageTheme.productSummary.displaySeparator,
    product.constituentProduct.isConstituentProduct,
  )

  const selectedContractDiscountedPrice = getSubscriptionPrice(
    basket,
    selectedVariant,
    selectedVariant?.price?.price.displayValue,
  )

  const variant =
    !PDPDisplayMasterProductTitle && selectedVariant ? selectedVariant : product

  const subtitle = variant.subtitle || product.subtitle

  const brandLogoProps = React.useMemo(() => {
    if (
      !product.brand?.imageUrl ||
      !product.brand?.name ||
      !showBrandLogoOnProductPage
    ) {
      return null
    }
    return {
      brandLabel: i18nText.brandLogoLabel(product.brand.name),
      imageUrl: product.brand.imageUrl,
      brandSize: BrandSize.LARGE,
      ...(enableProductBrandPageLink
        ? {
            brandUrl: product.brand.page?.path
              ? `${product.brand.page.path}${LIST_EXTENSION}`
              : i18nText.brandUrl([
                  originUrl,
                  `${product.brand.name
                    .replace(/\s/g, '-')
                    .replace('.', '')
                    .toLowerCase()}${LIST_EXTENSION}`,
                ]),
          }
        : {}),
    }
  }, [
    product,
    i18nText,
    enableProductBrandPageLink,
    originUrl,
    showBrandLogoOnProductPage,
  ])

  const onProductReviewClick = () => {
    transmit({
      type: 'columbo',
      payload: getColumboEventData({
        propertyArgs,
        argumentsObj: {
          '0': 'productReviewStars',
          '1': 'click',
        },
        requestArgs: {
          client_timestamp: Math.round(Date.now() / 1000),
          url: window.location.href,
        },
        eventData: {
          type: 'click',
          subtype: 'productReviewStars',
          contents: [],
        },
        nonce: EnterpriseCtx.metricNonce,
      }),
    })
  }

  if (!product) {
    return null
  }

  return (
    <React.Fragment>
      {brandLogoProps && <BrandLogo {...brandLogoProps} />}

      <DetailsContainer
        margin={theme.pageTheme?.productSummary?.margin}
        data-testid="product-details-container"
      >
        <ProductHeader
          displayMasterSKUTitle={displayMasterSKUTitle}
          selectedVariant={selectedVariant}
          product={product}
          variant={variant}
          hasShoeSizeMeId={hasShoeSizeMeId}
          isDesktop={isDesktop}
          showTagAndSubtitleBelowCta={showTagAndSubtitleBelowCta}
          subtitle={subtitle}
          theme={theme}
          showFlagsOnProduct={showFlagsOnProduct}
        />
      </DetailsContainer>

      <ProductReviews
        product={product}
        selectedVariant={selectedVariant}
        showPowerReview={showPowerReview}
        defaultLocale={defaultLocale}
        theme={theme}
        onProductReviewClick={onProductReviewClick}
        i18nText={i18nText}
      />

      {props.children}

      {hasLoyaltyEnabled && !hidePDPBuyingComponents && (
        <LoyaltyPointsMessage />
      )}
      <ProductPrice
        displayPriceAboveQuantityAndCheckConstituentLink={
          displayPriceAboveQuantityAndCheckConstituentLink
        }
        hidePDPBuyingComponents={hidePDPBuyingComponents}
        papMessageTopPosition={papMessageTopPosition}
        theme={theme}
        selectedVariant={selectedVariant}
        currency={currency}
        defaultLocale={defaultLocale}
        activeTabIndex={activeTabIndex}
        selectedContractDiscountedPrice={selectedContractDiscountedPrice}
        showProductSummaryPriceOnSingleLine={
          showProductSummaryPriceOnSingleLine
        }
        i18nText={i18nText}
        enablePricePerUnit={enablePricePerUnit}
        displayPPU={displayPPU}
        displayThemedSeperator={displayThemedSeperator}
        displayPriceContainerSeparators={displayPriceContainerSeparators}
        showPercentageDiscount={showPercentageDiscount}
      />
    </React.Fragment>
  )
}
